/** ROI handles, hit testing and handle-drag geometry updates. */
import { distToSegment, normalizeRect, pointInPolygon, roiBounds, snapAngle, translateRoi, type Roi } from '../core/roi';
import { toScreen, type View } from './transform';

export interface Handle {
  id: string;
  x: number;
  y: number;
  cursor: string;
}

const RECT_HANDLES: [string, number, number, string][] = [
  ['nw', 0, 0, 'nwse-resize'],
  ['n', 0.5, 0, 'ns-resize'],
  ['ne', 1, 0, 'nesw-resize'],
  ['e', 1, 0.5, 'ew-resize'],
  ['se', 1, 1, 'nwse-resize'],
  ['s', 0.5, 1, 'ns-resize'],
  ['sw', 0, 1, 'nesw-resize'],
  ['w', 0, 0.5, 'ew-resize'],
];

/** Handle positions in image coordinates. Width handles are offset in screen space by the caller. */
export function handlesOf(r: Roi, view: View): Handle[] {
  switch (r.kind) {
    case 'line': {
      const L = Math.hypot(r.x1 - r.x0, r.y1 - r.y0) || 1;
      const nx = -(r.y1 - r.y0) / L;
      const ny = (r.x1 - r.x0) / L;
      // Width handles sit at half the band width, but at least 10 CSS px from the line.
      const off = Math.max(r.width / 2, 10 / view.scale);
      const mx = (r.x0 + r.x1) / 2;
      const my = (r.y0 + r.y1) / 2;
      return [
        { id: 'p0', x: r.x0, y: r.y0, cursor: 'crosshair' },
        { id: 'p1', x: r.x1, y: r.y1, cursor: 'crosshair' },
        { id: 'w+', x: mx + nx * off, y: my + ny * off, cursor: 'row-resize' },
        { id: 'w-', x: mx - nx * off, y: my - ny * off, cursor: 'row-resize' },
      ];
    }
    case 'rect': {
      const b = normalizeRect(r);
      return RECT_HANDLES.map(([id, fx, fy, cursor]) => ({ id, x: b.x + fx * b.w, y: b.y + fy * b.h, cursor }));
    }
    case 'ellipse':
      return RECT_HANDLES.map(([id, fx, fy, cursor]) => ({
        id,
        x: r.cx - r.rx + fx * 2 * r.rx,
        y: r.cy - r.ry + fy * 2 * r.ry,
        cursor,
      }));
    case 'polygon':
      return r.pts.map((p, i) => ({ id: `v${i}`, x: p.x, y: p.y, cursor: 'move' }));
    case 'point':
      return [];
  }
}

export interface Hit {
  roi: Roi;
  handle: string; // handle id or 'body'
  cursor: string;
}

const HANDLE_R = 7;

/** Body hit test in screen space (tolerance in CSS px). */
function hitBody(r: Roi, sx: number, sy: number, view: View, tol = 6): boolean {
  const p = (x: number, y: number) => toScreen(view, x, y);
  switch (r.kind) {
    case 'line': {
      const [ax, ay] = p(r.x0, r.y0);
      const [bx, by] = p(r.x1, r.y1);
      return distToSegment(sx, sy, ax, ay, bx, by) <= Math.max(tol, (r.width * view.scale) / 2);
    }
    case 'rect': {
      const b = normalizeRect(r);
      const [x0, y0] = p(b.x, b.y);
      const [x1, y1] = p(b.x + b.w, b.y + b.h);
      return sx >= x0 - tol && sx <= x1 + tol && sy >= y0 - tol && sy <= y1 + tol;
    }
    case 'ellipse': {
      const [cx, cy] = p(r.cx, r.cy);
      const rx = Math.abs(r.rx) * view.scale + tol;
      const ry = Math.abs(r.ry) * view.scale + tol;
      return ((sx - cx) / rx) ** 2 + ((sy - cy) / ry) ** 2 <= 1;
    }
    case 'polygon': {
      const pts = r.pts.map((q) => {
        const [x, y] = p(q.x, q.y);
        return { x, y };
      });
      if (pointInPolygon(sx, sy, pts)) return true;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        if (distToSegment(sx, sy, a.x, a.y, b.x, b.y) <= tol) return true;
      }
      return false;
    }
    case 'point': {
      const [x, y] = p(r.x, r.y);
      return Math.hypot(sx - x, sy - y) <= Math.max(10, (r.size * view.scale) / 2 + tol);
    }
  }
}

/** Find the ROI part under a screen point: handles of the selected ROI first, then bodies (topmost first). */
export function hitTest(rois: Roi[], selectedId: string | null, sx: number, sy: number, view: View): Hit | null {
  const sel = rois.find((r) => r.id === selectedId && r.visible);
  if (sel && !sel.locked) {
    for (const h of handlesOf(sel, view)) {
      const [hx, hy] = toScreen(view, h.x, h.y);
      if (Math.abs(sx - hx) <= HANDLE_R && Math.abs(sy - hy) <= HANDLE_R) return { roi: sel, handle: h.id, cursor: h.cursor };
    }
  }
  for (let i = rois.length - 1; i >= 0; i--) {
    const r = rois[i];
    if (!r.visible) continue;
    if (hitBody(r, sx, sy, view)) return { roi: r, handle: 'body', cursor: r.locked ? 'pointer' : 'move' };
  }
  return null;
}

export interface DragOpts {
  /** Constrain line angle to 15° steps / squares / circles. */
  shift: boolean;
  /** Resize symmetrically about the centre. */
  alt: boolean;
  snap: (v: number) => number;
}

/** New geometry for dragging `handle` of the ROI (as it was at drag start) by image delta. */
export function dragHandle(orig: Roi, handle: string, x: number, y: number, dx: number, dy: number, o: DragOpts): Roi {
  if (handle === 'body') {
    const moved = translateRoi(orig, dx, dy);
    // Snap a reference point and keep the shape: the start of a line, the position of a point,
    // the top-left of an area ROI's bounding box (o.snap is centre- or edge-snapping accordingly).
    const ref =
      moved.kind === 'line'
        ? { x: moved.x0, y: moved.y0 }
        : moved.kind === 'point'
          ? { x: moved.x, y: moved.y }
          : { x: roiBounds(moved).x, y: roiBounds(moved).y };
    return translateRoi(moved, o.snap(ref.x) - ref.x, o.snap(ref.y) - ref.y);
  }
  switch (orig.kind) {
    case 'line': {
      if (handle === 'p0' || handle === 'p1') {
        const other = handle === 'p0' ? { x: orig.x1, y: orig.y1 } : { x: orig.x0, y: orig.y0 };
        let p = { x: o.snap(x), y: o.snap(y) };
        if (o.shift) p = snapAngle(other.x, other.y, p.x, p.y, 15);
        return handle === 'p0' ? { ...orig, x0: p.x, y0: p.y } : { ...orig, x1: p.x, y1: p.y };
      }
      if (handle === 'w+' || handle === 'w-') {
        const L = Math.hypot(orig.x1 - orig.x0, orig.y1 - orig.y0) || 1;
        const d = Math.abs(((x - orig.x0) * -(orig.y1 - orig.y0) + (y - orig.y0) * (orig.x1 - orig.x0)) / L);
        return { ...orig, width: Math.max(1, Math.round(2 * d)) };
      }
      return orig;
    }
    case 'rect':
    case 'ellipse': {
      const b =
        orig.kind === 'rect'
          ? normalizeRect(orig)
          : { x: orig.cx - Math.abs(orig.rx), y: orig.cy - Math.abs(orig.ry), w: 2 * Math.abs(orig.rx), h: 2 * Math.abs(orig.ry) };
      let x0 = b.x;
      let y0 = b.y;
      let x1 = b.x + b.w;
      let y1 = b.y + b.h;
      const px = o.snap(x);
      const py = o.snap(y);
      if (handle.includes('w')) x0 = px;
      if (handle.includes('e')) x1 = px;
      if (handle.includes('n')) y0 = py;
      if (handle.includes('s')) y1 = py;
      if (o.alt) {
        const cx = b.x + b.w / 2;
        const cy = b.y + b.h / 2;
        if (handle.includes('w')) x1 = 2 * cx - x0;
        if (handle.includes('e')) x0 = 2 * cx - x1;
        if (handle.includes('n')) y1 = 2 * cy - y0;
        if (handle.includes('s')) y0 = 2 * cy - y1;
      }
      if (o.shift && handle.length === 2) {
        const s = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
        if (handle.includes('w')) x0 = x1 - s * Math.sign(x1 - x0 || 1);
        else x1 = x0 + s * Math.sign(x1 - x0 || 1);
        if (handle.includes('n')) y0 = y1 - s * Math.sign(y1 - y0 || 1);
        else y1 = y0 + s * Math.sign(y1 - y0 || 1);
      }
      const nx = Math.min(x0, x1);
      const ny = Math.min(y0, y1);
      const w = Math.abs(x1 - x0);
      const h = Math.abs(y1 - y0);
      if (orig.kind === 'rect') return { ...orig, x: nx, y: ny, w, h };
      return { ...orig, cx: nx + w / 2, cy: ny + h / 2, rx: w / 2, ry: h / 2 };
    }
    case 'polygon': {
      const i = parseInt(handle.slice(1), 10);
      const pts = orig.pts.map((p, k) => (k === i ? { x: o.snap(x), y: o.snap(y) } : p));
      return { ...orig, pts };
    }
    case 'point':
      return { ...orig, x: o.snap(x), y: o.snap(y) };
  }
}

/** Snap to the nearest pixel centre (i + 0.5). */
export function snapToCenter(v: number): number {
  return Math.floor(v) + 0.5;
}
